import React, { useState } from 'react';
import { PiCheckCircleFill } from "react-icons/pi";
import { MdOutlinePersonalVideo } from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";
import { AiOutlineDown, AiOutlineUp } from "react-icons/ai";
import { message, Modal } from 'antd';
import { useSnapshot } from 'valtio';
import { CustomTemplateStore } from '@/utils/store/customTemplate';

interface TemplateCardUDProps {
  id?: string;
  title: string;
  info: string;
  userId: string;
  value: string;
  curKey: string;
  sk: (value: string) => void;
}

const TemplateCardUD: React.FC<TemplateCardUDProps> = ({ 
  id,
  title, 
  info, 
  userId, 
  value, 
  curKey, 
  sk 
}) => {
  const isSelect = value === curKey;
  const { deleteLoading } = useSnapshot(CustomTemplateStore);
  const [infoModalVisible, setInfoModalVisible] = useState(false);
  
  // 截断描述文本，最多显示40个字符
  const truncatedInfo = info.length > 28 ? `${info.substring(0, 28)}...` : info;

  // 展开详细信息
  const handleExpand = (e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止事件冒泡
    setInfoModalVisible(true);
  };

  // 删除模板函数
  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止事件冒泡
    
    if (!id) {
      message.error('模板ID不存在');
      return;
    }

    Modal.confirm({
      title: '确认删除',
      content: `确认要删除模板"${title}"吗？此操作不可撤销。`,
      okText: '确认删除',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        const success = await CustomTemplateStore.deleteTemplate(id, userId);
        if (success) {
          message.success('模板删除成功');
          // 如果当前选中的模板被删除，清除选中状态
          if (isSelect) {
            sk('');
          }
        }
      }
    });
  };

  return (
    <div 
      className={`border rounded-2xl p-5 flex flex-col cursor-pointer transition-all relative
        ${isSelect ? "border-black shadow-lg" : "border-[#E9E9E7] hover:border-[#D1D1CF]"}`}
      onClick={() => sk(value)}
    >
      {/* 图片占位区域 - 用图标代替 */}
      <div className="w-full aspect-[4/3] bg-[#F7F7F5] rounded-xl overflow-hidden mb-4 flex items-center justify-center relative">
        <MdOutlinePersonalVideo className="text-4xl text-[#91918E] opacity-60" />
        {/* 删除按钮 - 悬浮在图片区域右上角 */}
        <button
          onClick={handleDelete}
          disabled={deleteLoading}
          className="absolute top-2 right-2 p-1.5 rounded-full bg-white/80 hover:bg-red-50 text-[#91918E] hover:text-red-500 transition-colors duration-200 opacity-0 group-hover:opacity-100"
          title="删除模板"
        >
          <RiDeleteBin6Line className="w-3.5 h-3.5" />
        </button>
      </div>
      <div>
        <div className="flex items-center gap-1.5 mb-1">
          <h3 className="text-sm font-bold" title={title}>
            {title.length > 12 ? `${title.substring(0, 12)}...` : title}
          </h3>
          <span className="bg-blue-100 text-blue-600 text-[9px] px-1.5 py-0.5 rounded-full">自定义</span>
          {isSelect && <PiCheckCircleFill className="text-base text-[#4B5563]" />}
        </div>
        <p className="text-[11px] text-[#91918E] leading-relaxed" title={info}>
          {truncatedInfo}
        </p>
      </div>
      
      {/* 展开按钮 - 右下角 */}
      <div className="mt-auto pt-2 flex justify-end">
        <button
          onClick={handleExpand}
          className="flex items-center gap-1 px-2 py-1 bg-[#F7F7F5] hover:bg-[#EFEFED] rounded-full text-[10px] text-[#91918E] hover:text-[#73726E] transition-all duration-200"
          title="展开详细信息"
        >
          展开
        </button>
      </div>
      
      {/* 信息展示模态框 */}
      <Modal
        title={`模板详细信息 - ${title}`}
        open={infoModalVisible}
        onCancel={() => setInfoModalVisible(false)}
        footer={[
          <button
            key="close"
            onClick={() => setInfoModalVisible(false)}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors duration-200"
          >
            关闭
          </button>
        ]}
        width={600}
      >
        <div className="py-4">
          <div className="text-gray-700 whitespace-pre-wrap leading-relaxed">
            {info || '暂无详细信息'}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TemplateCardUD;
